import React, { useEffect, useState } from 'react';

import { useRouter } from 'next/router';
import { usePostHog } from 'posthog-js/react';

import {
  Form,
  Button,
  TextFieldControlled as Input,
  ToggleFieldControlled as Toggle,
} from '@leafwell/components';

import { useBrazeContext } from 'contexts/braze-context';
import { useEventsTracker } from 'contexts/eventsTracker';
import { useWordPressContext } from 'contexts/wordpress-provider';

export type EmailForm = {
  formId: string | null;
  formSubmitText?: string | null;
  formSubmitUrl?: string | null;
  campaign?: string | null;
};

const EmailForm: React.FC<EmailForm> = ({
  formId,
  formSubmitText,
  formSubmitUrl,
  campaign,
}) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [promotion, setPromotion] = useState<string | null>(null);
  const { asPath, query } = useRouter();
  const posthog = usePostHog();
  const { trackEvent } = useEventsTracker();
  const { saveUser: saveBrazeUser, requestImmediateDataFlush } =
    useBrazeContext();
  const { translations } = useWordPressContext();

  const product: 'membership' | 'telepharmacist' | null = asPath.includes(
    '/membership',
  )
    ? 'membership'
    : asPath.includes('/cannabis-care')
    ? 'telepharmacist'
    : null;

  const heroName = asPath?.split('?');

  useEffect(() => {
    if (query.utm_campaign && query.utm_campaign != '') {
      if (query.utm_campaign.includes('popup_tallahassee_september2025')) {
        setPromotion('14.99');
      } else if (query.utm_campaign.includes('popup_tampa_september2025')) {
        setPromotion('19.99');
      }
    }
  }, [query]);

  async function handleSubmit({ accepted_terms_of_service, email }) {
    if (accepted_terms_of_service) {
      setIsLoading(true);
      // https://posthog.com/docs/product-analytics/identify#alias-assigning-multiple-distinct-ids-to-the-same-user
      posthog.alias(email);

      const customAttributes = asPath.includes('florida')
        ? {
            membership_promo_price: promotion,
            membership_promo_state: 'Florida',
            membership_promo_join: new Date().toISOString(),
          }
        : {};

      saveBrazeUser({
        email,
        customAttributes,
      });

      trackEvent({
        event: 'form_submit',
        form_name: `email_${window?.location?.pathname}`,
        form_destination: formSubmitUrl
          ? formSubmitUrl
          : window?.location?.pathname,
        form_submit_text: formSubmitText
          ? formSubmitText
          : translations['Get in touch'],
        formType: 'email',
        email,
        campaign,
      });

      if (product) {
        trackEvent({
          event: 'generate_lead',
          item_id: `Hero${heroName[0]?.replace('/', '-')}`,
          service: product,
          page_referrer: document.referrer,
        });
      }

      requestImmediateDataFlush();

      if (formSubmitUrl) {
        //we need to delay the redirect to the MC domain to assure the user in Braze is correctly created with its customer attributes
        setTimeout(() => {
          window.location.assign(formSubmitUrl);
        }, 1_500);
        setIsLoading(false);
      } else {
        setIsLoading(false);
        setFormSubmitted(true);
      }
    }
  }

  return (
    <Form
      id={formId}
      className="gap-y-4"
      mode="onSubmit"
      defaultValues={{
        email: '',
      }}
      onSubmit={handleSubmit}
    >
      {formSubmitted && !formSubmitUrl ? (
        <p className="text-center">
          Thank you for your enquiry.
          <br />
          One of our team will be in touch with you as soon as possible.
        </p>
      ) : (
        <>
          <div className="grid gap-y-2">
            <Input
              className="rounded-lg"
              type="email"
              name="email"
              placeholder={`Enter your email *`}
              validations={['required', 'email']}
            />
          </div>
          <Toggle
            className="border-gray-300"
            type="checkbox"
            name="accepted_terms_of_service"
            validations={['required']}
            options={[
              {
                label: (
                  <p className="text-sm">
                    {translations['I have read and accept the']}{' '}
                    <a target="_blank" href="https://leafwell.com/terms-of-use">
                      {translations['terms of service']}
                    </a>{' '}
                    {translations['and']}{' '}
                    <a
                      target="_blank"
                      href="https://leafwell.com/privacy-policy"
                    >
                      {translations['privacy policy']}
                    </a>
                  </p>
                ),
                value: '1',
              },
            ]}
          />
          <Button
            type="submit"
            label={
              formSubmitText ? formSubmitText : translations['Get in touch']
            }
            name="submit"
            loading={isLoading}
            variant="secondary"
            className="w-full rounded-lg mt-8 md:w-fit md:px-20 md:justify-self-end"
          />
        </>
      )}
    </Form>
  );
};

export default EmailForm;
